<template>
  <v-card dark v-if="phase.editing" class="opportunity-phase editing mt-2" key="editing">
    <v-toolbar card class="transparent">
      <v-toolbar-side-icon v-if="phase.initial">
        <v-icon>mdi-circle-outline</v-icon>
      </v-toolbar-side-icon>

      <v-toolbar-side-icon v-if="!phase.next">
        <v-icon>mdi-checkbox-marked-circle-outline</v-icon>
      </v-toolbar-side-icon>

      <v-toolbar-side-icon v-if="phase.next && !phase.initial">
        <v-icon>mdi-arrow-down</v-icon>
      </v-toolbar-side-icon>
      <v-toolbar-title>
        <div class="mt-4">
          <div class="title">{{ phase.name }}</div>
          <p class="subheading">{{ phase.description }}</p>
        </div>
      </v-toolbar-title>

      <v-spacer />

      <can I="edit" a="Opportunity">
        <v-menu left max-width="30vw" v-model="removing">
          <v-tooltip top slot="activator" v-if="!phase.initial && phase.next">
            <v-btn flat fab slot="activator" color="error">
              <v-icon>mdi-delete</v-icon>
            </v-btn>
            <span>Remove</span>
          </v-tooltip>

          <v-card>
            <v-card-title class="headline">Are you sure?</v-card-title>
            <v-card-text>
              <p>
                This will permanently remove all phase associated behaviors and data.
              </p>
            </v-card-text>
            <v-card-actions>
              <v-btn flat block @click="removing = false" color="grey">
                cancel
              </v-btn>
              <v-btn block @click="removePhase" color="error">
                Delete
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-menu>
      </can>
    </v-toolbar>

    <v-card-text>
      <v-form v-model="valid" @submit.prevent="submit">
        <v-text-field label="Name" v-model="phase.name" required :rules="rules.name" />
        <v-text-field
          v-model="phase.description"
          label="Description"
          required
          :rules="rules.description"
        />
      </v-form>
    </v-card-text>

    <v-card-actions>
      <v-spacer />
      <v-btn flat @click="cancel" color="grey">
        Cancel
      </v-btn>
      <v-btn @click="submit" color="primary" :disabled="!valid || submitting">
        Update
      </v-btn>
    </v-card-actions>
  </v-card>

  <v-card v-else class="opportunity-phase viewing mt-2" key="viewing">
    <v-toolbar card class="transparent">
      <v-toolbar-side-icon v-if="phase.initial">
        <v-icon>mdi-circle-outline</v-icon>
      </v-toolbar-side-icon>
      <v-toolbar-side-icon v-if="!phase.next">
        <v-icon>mdi-checkbox-marked-circle-outline</v-icon>
      </v-toolbar-side-icon>
      <v-toolbar-side-icon v-if="phase.next && !phase.initial">
        <v-icon>mdi-arrow-down</v-icon>
      </v-toolbar-side-icon>
      <v-toolbar-title>
        <div class="mt-4">
          <div class="title">{{ phase.name }}</div>
          <p class="subheading">{{ phase.description }}</p>
        </div>
      </v-toolbar-title>

      <v-spacer />

      <can I="edit" a="Opportunity">
        <v-menu left>
          <v-tooltip top slot="activator">
            <v-btn small flat fab slot="activator">
              <v-icon>mdi-plus</v-icon>
            </v-btn>
            <span>Add behavior</span>
          </v-tooltip>

          <v-list>
            <v-list-tile @click="addBehavior('OpportunityPhaseBehavior::EmailCommunication')">
              <v-list-tile-title>Email Communication</v-list-tile-title>
            </v-list-tile>
            <v-list-tile
              @click="addBehavior('OpportunityPhaseBehavior::SuccessfulReminder')"
              v-if="canDisplayReminder()"
            >
              <v-list-tile-title>Successful Reminder</v-list-tile-title>
            </v-list-tile>
            <v-list-tile @click="addBehavior('OpportunityPhaseBehavior::Withdrawal')">
              <v-list-tile-title>Withdrawal</v-list-tile-title>
            </v-list-tile>
            <v-list-tile @click="addBehavior('OpportunityPhaseBehavior::Questionnaire')">
              <v-list-tile-title>Questionnaire</v-list-tile-title>
            </v-list-tile>
            <v-list-tile @click="addBehavior('OpportunityPhaseBehavior::PsychometricAssessment')">
              <v-list-tile-title>Assessment</v-list-tile-title>
            </v-list-tile>
            <v-list-tile @click="addBehavior('OpportunityPhaseBehavior::FinancialAssessment')">
              <v-list-tile-title>Household Income Survey</v-list-tile-title>
            </v-list-tile>
            <v-list-tile
              @click="addBehavior('OpportunityPhaseBehavior::SecondaryTranscript')"
              v-if="
                phase.initial &&
                  phase.behaviors.filter(
                    b => b.type == 'OpportunityPhaseBehavior::SecondaryTranscript'
                  ).length == 0
              "
            >
              <v-list-tile-title>Require Secondary Education Transcript</v-list-tile-title>
            </v-list-tile>
          </v-list>
        </v-menu>
      </can>

      <can I="edit" a="Opportunity">
        <v-tooltip top>
          <v-btn small fab slot="activator" @click="edit">
            <v-icon>mdi-pencil</v-icon>
          </v-btn>
          <span>Edit</span>
        </v-tooltip>
      </can>

      <can I="edit" a="Opportunity">
        <v-menu left>
          <v-tooltip top slot="activator">
            <v-btn small fab color="secondary" slot="activator">
              <v-icon>mdi-plus</v-icon>
            </v-btn>
            <span>Add Phase</span>
          </v-tooltip>

          <v-list>
            <v-list-tile @click="prependPhase(phase)" v-if="!phase.initial">
              <v-list-tile-title>Prepend New Phase</v-list-tile-title>
            </v-list-tile>
            <v-list-tile @click="appendPhase(phase)" v-if="phase.next">
              <v-list-tile-title>Append New Phase</v-list-tile-title>
            </v-list-tile>
          </v-list>
        </v-menu>
      </can>
    </v-toolbar>

    <PhaseBehavior
      v-for="behavior in phase.behaviors"
      :value="behavior"
      :key="`phase-behavior=${behavior.id}`"
    />
  </v-card>
</template>

<script>
// Components
import PhaseBehavior from '@/components/opportunities/PhaseBehavior'

// Vue Component
export default {
  components: { PhaseBehavior },
  props: {
    phase: {
      type: Object,
      default: null,
    },
  },

  data() {
    return {
      removing: false,
      submitting: false,
      // Validations
      valid: true,
      rules: {
        name: [v => !!v || 'must be specified', v => (v && v.length < 25) || 'too long'],
        description: [v => !!v || 'must be specified'],
      },
    }
  },

  methods: {
    canDisplayReminder() {
      return (
        !this.phase.next &&
        !this.phase.behaviors.some(b => {
          return b.type === 'OpportunityPhaseBehavior::SuccessfulReminder'
        })
      )
    },
    edit() {
      this.phase.editing = true
    },
    cancel() {
      this.$store.dispatch('admin/opportunities/fetch', this.$route.params.id)
    },
    submit() {
      this.submitting = true
      this.$store
        .dispatch('admin/opportunities/updatePhase', this.phase)
        .then(
          () => this.$store.dispatch('snackbarSuccess', 'Opportunity Phase Updated'),
          () => this.$store.dispatch('snackbarError', 'Could not update Opportunity Phase')
        )
        .then(() => this.$store.dispatch('admin/opportunities/fetch', this.$route.params.id))
        .finally(() => (this.submitting = false))
    },
    removePhase() {
      this.$store
        .dispatch('admin/opportunities/removePhase', this.phase)
        .then(
          () => this.$store.dispatch('snackbarSuccess', 'Opportunity Phase Removed'),
          () => this.$store.dispatch('snackbarError', 'Could not remove Opportunity Phase')
        )
    },
    prependPhase(phase) {
      this.$store
        .dispatch('admin/opportunities/createPhase', {
          opportunity: { id: this.$route.params.id },
          action: { phase_id: phase.id, type: 'prepend' },
        })
        .then(() =>
          this.$store.commit('snackbar', {
            text: 'Opportunity phase added',
            type: 'success',
          })
        )
    },
    appendPhase(phase) {
      this.$store
        .dispatch('admin/opportunities/createPhase', {
          opportunity: { id: this.$route.params.id },
          action: { phase_id: phase.id, type: 'append' },
        })
        .then(() =>
          this.$store.commit('snackbar', {
            text: 'Opportunity phase added',
            type: 'success',
          })
        )
    },
    addBehavior(type) {
      this.phase.behaviors.push({
        id: null,
        phase: {
          id: this.phase.id,
          opportunity: { id: this.$route.params.id },
        },
        type: type,
        entry: true,
        content: null,
        configuration: {},
      })
    },
  },
}
</script>
